import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mic, Square, Copy, Trash2, Plus, Loader2, StickyNote } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export const NoteList = () => {
  const qc = useQueryClient();
  const [testo, setTesto] = useState("");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const { data: note = [], isLoading } = useQuery({
    queryKey: ["note"],
    queryFn: async () => (await api.get("/note")).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["note"] });

  const salva = async () => {
    const t = testo.trim();
    if (!t) return;
    setSaving(true);
    try { await api.post("/note", { testo: t, source: "text" }); setTesto(""); refresh(); toast.success("Nota salvata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const elimina = async (id) => {
    try { await api.delete(`/note/${id}`); refresh(); toast.success("Nota eliminata"); }
    catch (e) { toast.error("Errore"); }
  };

  const copia = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Nota copiata negli appunti"); }
    catch (e) { toast.error("Copia non riuscita"); }
  };

  const transcribe = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "nota.webm");
      const { data } = await api.post("/note/trascrivi", fd);
      if (data.text) { setTesto((p) => (p ? p + " " : "") + data.text); toast.success("Nota vocale trascritta"); }
      else toast.info("Nessun parlato rilevato");
    } catch (e) { toast.error("Trascrizione non riuscita"); }
    finally { setBusy(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => { stream.getTracks().forEach((t) => t.stop()); await transcribe(new Blob(chunksRef.current, { type: "audio/webm" })); };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Microfono non disponibile o permesso negato"); }
  };
  const stopRec = () => { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); setRecording(false); };

  return (
    <div className="space-y-4" data-testid="note-list">
      <div className="gr-card p-3.5 sm:p-4">
        <Textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={4} placeholder="Scrivi qui un appunto... oppure registra una nota vocale" className="min-h-[110px] text-base" data-testid="nota-testo" />
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          {!recording
            ? <Button type="button" variant="outline" onClick={startRec} disabled={busy} className="gr-mobile-tap" data-testid="nota-rec-start"><Mic size={16} className="mr-1.5" /> {busy ? "Trascrizione..." : "Nota vocale"}</Button>
            : <Button type="button" onClick={stopRec} className="gr-mobile-tap bg-red-600 hover:bg-red-700 text-white" data-testid="nota-rec-stop"><span className="h-2.5 w-2.5 rounded-full bg-white animate-pulse mr-2" /> <Square size={14} className="mr-1.5" /> Ferma e trascrivi</Button>}
          {busy && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Loader2 size={14} className="animate-spin" /> Sto trascrivendo…</span>}
          <Button type="button" onClick={salva} disabled={saving || !testo.trim()} className="font-semibold gr-mobile-tap ml-auto" data-testid="nota-salva"><Plus size={16} className="mr-1" /> Salva nota</Button>
        </div>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Caricamento...</div>}
      {!isLoading && note.length === 0 && (
        <div className="text-center py-10 text-muted-foreground"><StickyNote size={28} className="mx-auto mb-2 opacity-50" /><p className="text-sm">Nessuna nota. Scrivine una o registra una nota vocale.</p></div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {note.map((n) => (
          <div key={n.id} className="gr-card p-3.5 flex flex-col" data-testid={`nota-card-${n.id}`}>
            <div className="text-sm whitespace-pre-wrap break-words flex-1">{n.testo}</div>
            <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t">
              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                {n.source === "voice" && <Mic size={11} />}
                {new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => copia(n.testo)} title="Copia" data-testid={`nota-copia-${n.id}`}><Copy size={15} className="text-primary" /></Button>
                <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => elimina(n.id)} title="Elimina" data-testid={`nota-elimina-${n.id}`}><Trash2 size={15} className="text-destructive" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
